
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Tag, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

interface SubscriptionInfoProps {
  subscription: {
    categories: Array<{ id: string; name: string; }>;
    deliveryTimes: Array<{ id: string; time: string; categoryId?: string; }>;
  } | null;
  hasActiveSubscription: boolean;
}

export function SubscriptionInfo({ subscription, hasActiveSubscription }: SubscriptionInfoProps) {
  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
    return format(date, 'h:mm a');
  };

  if (!hasActiveSubscription) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-center text-center py-4">
            <AlertTriangle className="h-5 w-5 mr-2 text-amber-500" />
            <p className="text-muted-foreground">This subscriber doesn't have an active subscription</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center">
          <Tag className="h-4 w-4 mr-2" />
          Subscription Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-2">Subscribed Categories</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {subscription?.categories.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categories selected</p>
            ) : (
              subscription?.categories.map((category) => (
                <Badge key={category.id} variant="outline">
                  {category.name}
                </Badge>
              ))
            )}
          </div>
        </div>
        
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-2">Delivery Times</p>
          {subscription?.deliveryTimes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No delivery times set</p>
          ) : (
            <div className="space-y-2">
              {subscription?.deliveryTimes.map((time) => {
                const categoryName = subscription?.categories.find(
                  c => c.id === time.categoryId
                )?.name || 'Random';
                
                return (
                  <div key={time.id} className="flex items-center p-2 border rounded-md">
                    <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span className="font-medium mr-2">{formatTime(time.time)}</span>
                    <span className="text-sm text-muted-foreground">
                      {time.categoryId ? `from ${categoryName}` : 'from Random category'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
